import {
    EXPORT_STEP_FILE_RESOURCE,
    EXPORT_STEP_MAP_LAYER,
    EXPORT_STEP_SUMMARY,
    EXPORT_STEP_UPLOAD,
    EXPORT_TITLE_PREFIX
} from '../../constants';
import useTranslation from '../useTranslation';
import { useCallback, useEffect, useState } from 'react';
import type { JohnDeereMapLayerMetadata } from 'types/johnDeere';
import { computeExtent } from 'utils/geometryExtent';
import { parseJohnDeereTag } from 'utils/johnDeereTag';
import { renderRateMapPng } from 'utils/rateMapPngRender';

import useApplicationChemicals from 'hooks/useApplicationChemicals';
import type { ApplicationChemical } from 'hooks/useApplicationChemicals/types';
import useFetch from 'hooks/useFetch';
import useJohnDeereFields from 'hooks/useJohnDeereFields';
import useJohnDeereMapExport from 'hooks/useJohnDeereMapExport';
import useJohnDeereOrganizations from 'hooks/useJohnDeereOrganizations';
import { useToast } from 'hooks/useToast';

import { DEFAULT_LEGEND, JOHN_DEERE_FIELDS_API, ORGANIZATIONS_API } from './constants';
import type {
    AutoResolveResult,
    JohnDeereExportTarget,
    JohnDeereField,
    JohnDeereOrganization,
    ModalState,
    UseDataParams,
    UseDataReturn
} from './types';

const JD_API_BASE_URL = import.meta.env.VITE_JD_API_BASE_URL || '';

const useData = ({
    application,
    boundary,
    flights,
    isOpen,
    onClose,
    telemetryPointsByFlightId
}: UseDataParams): UseDataReturn => {
    const [state, setState] = useState<ModalState>({ kind: 'resolving' });
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>();
    const [selectedFieldId, setSelectedFieldId] = useState<string | undefined>();

    const { ERROR_GENERIC } = useTranslation();
    const { broadcastToast } = useToast();
    const {
        fetch: fetchOrganizations,
        isLoading: isOrganizationsLoading,
        organizations
    } = useJohnDeereOrganizations();
    const { fetch: fetchFields, fields, isLoading: isFieldsLoading } = useJohnDeereFields();
    const { exportMapLayer } = useJohnDeereMapExport();
    const { fetchApplicationChemicals, fetchApplicationMix } = useApplicationChemicals();

    const { authenticatedGet } = useFetch({
        apiHost: JD_API_BASE_URL,
        headers: { 'Content-Type': 'application/json' }
    });

    const findFieldAcrossOrganizations = useCallback(
        async (shortId: string): Promise<AutoResolveResult> => {
            const organizationsResponse =
                await authenticatedGet<JohnDeereOrganization[]>(ORGANIZATIONS_API);
            const fetchedOrganizations = organizationsResponse.data ?? [];

            const fieldLookups = await Promise.all(
                fetchedOrganizations.map(async organization => {
                    const response = await authenticatedGet<JohnDeereField[]>(
                        `${JOHN_DEERE_FIELDS_API}/${organization.id}/fields`
                    );

                    return { fields: response.data ?? [], organizationId: organization.id };
                })
            );

            const matches: { fieldName: string; target: JohnDeereExportTarget }[] = [];

            for (const lookup of fieldLookups) {
                for (const field of lookup.fields) {
                    if (field.id.startsWith(shortId)) {
                        matches.push({
                            fieldName: field.name,
                            target: { fieldId: field.id, organizationId: lookup.organizationId }
                        });
                    }
                }
            }

            if (matches.length === 1) {
                return {
                    fieldName: matches[0].fieldName,
                    kind: 'matched',
                    target: matches[0].target
                };
            }

            return { kind: 'noMatch' };
        },
        [authenticatedGet]
    );

    const runAutoResolve = useCallback(async () => {
        setState({ kind: 'resolving' });

        const parsed = parseJohnDeereTag(application.site?.name ?? '');

        if (!parsed) {
            await fetchOrganizations();
            setState({ kind: 'needsPicker' });

            return;
        }

        const result = await findFieldAcrossOrganizations(parsed.shortId);

        if (result.kind === 'matched') {
            setState({ fieldName: result.fieldName, kind: 'autoResolved', target: result.target });

            return;
        }

        await fetchOrganizations();
        setState({ kind: 'needsPicker' });
    }, [application.site?.name, fetchOrganizations, findFieldAcrossOrganizations]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        runAutoResolve();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const buildChemicalMetadata = useCallback(
        (chemicals: ApplicationChemical[], mixName: string | null): JohnDeereMapLayerMetadata[] => {
            const entries: JohnDeereMapLayerMetadata[] = [];

            if (mixName) {
                entries.push({ name: 'Mix', value: mixName });
            }

            chemicals.forEach((chemical, index) => {
                const rate = `${chemical.applicationRate} ${chemical.rateUnit}/${chemical.ratePerUnit}`;
                const epa = chemical.epaNumber ? ` · EPA ${chemical.epaNumber}` : '';

                entries.push({
                    name: `Product ${index + 1}`,
                    value: `${chemical.labelName} @ ${rate}${epa}`
                });
            });

            return entries;
        },
        []
    );

    const runExport = useCallback(
        async (target: JohnDeereExportTarget) => {
            try {
                setState({ kind: 'exporting', step: EXPORT_STEP_SUMMARY, target });

                const boundaryPart = boundary.boundaryParts?.[0]?.boundary;

                if (!boundaryPart) {
                    throw new Error('Boundary has no geometry parts');
                }

                const extent = computeExtent(boundaryPart);

                const [chemicals, attachedMix] = await Promise.all([
                    fetchApplicationChemicals({ applicationId: application.id }),
                    fetchApplicationMix({ applicationId: application.id })
                ]);
                const metadata = buildChemicalMetadata(
                    chemicals ?? [],
                    attachedMix?.mix?.name ?? null
                );

                setState({ kind: 'exporting', step: EXPORT_STEP_MAP_LAYER, target });

                const image = await renderRateMapPng({
                    boundary: boundaryPart,
                    extent,
                    flights,
                    legend: DEFAULT_LEGEND,
                    telemetryPointsByFlightId
                });

                setState({ kind: 'exporting', step: EXPORT_STEP_FILE_RESOURCE, target });

                const title = `${EXPORT_TITLE_PREFIX} - ${application.name ?? application.id}`;

                setState({ kind: 'exporting', step: EXPORT_STEP_UPLOAD, target });

                const response = await exportMapLayer({
                    extent,
                    fieldId: target.fieldId,
                    image,
                    legend: DEFAULT_LEGEND,
                    metadata,
                    organizationId: target.organizationId,
                    text: title,
                    title
                });

                setState({ kind: 'success', viewUrl: response?.viewUrl });
            } catch (caughtError) {
                const message =
                    caughtError instanceof Error && caughtError.message
                        ? caughtError.message
                        : ERROR_GENERIC;

                setState({ kind: 'error', message });
                await broadcastToast({ text: message, type: 'error' });
            }
        },
        [
            application.id,
            application.name,
            boundary,
            broadcastToast,
            buildChemicalMetadata,
            exportMapLayer,
            fetchApplicationChemicals,
            fetchApplicationMix,
            flights,
            telemetryPointsByFlightId,
            ERROR_GENERIC
        ]
    );

    const onConfirmExport = useCallback(() => {
        if (state.kind === 'autoResolved') {
            runExport(state.target);

            return;
        }

        if (state.kind === 'needsPicker' && selectedOrganizationId && selectedFieldId) {
            runExport({ fieldId: selectedFieldId, organizationId: selectedOrganizationId });
        }
    }, [runExport, selectedFieldId, selectedOrganizationId, state]);

    const onOrganizationSelect = useCallback(
        (organizationId: string) => {
            setSelectedOrganizationId(organizationId);
            setSelectedFieldId(undefined);
            fetchFields({ organizationId });
        },
        [fetchFields]
    );

    const onFieldSelect = useCallback((fieldId: string) => {
        setSelectedFieldId(fieldId);
    }, []);

    const onCancel = useCallback(() => {
        onClose();
    }, [onClose]);

    const onRetry = useCallback(() => {
        runAutoResolve();
    }, [runAutoResolve]);

    return {
        fields,
        isFieldsLoading,
        isOrganizationsLoading,
        onCancel,
        onConfirmExport,
        onFieldSelect,
        onOrganizationSelect,
        onRetry,
        organizations,
        selectedFieldId,
        selectedOrganizationId,
        state
    };
};

export default useData;
